import { PaperPlaneTilt } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

/**
 * V9.27 — Inline confirmation chip rendered below the assistant message
 * that just delegated a Q&A task to a contact via `delegate_to_contact`.
 *
 * Tap → /tasks. The chip mirrors the visual language of
 * `ReminderCreatedChip` but uses a paper-plane icon to read clearly as
 * "outbound delegation" rather than "scheduled reminder".
 */
export default function TaskDelegatedChip({ task }) {
  const navigate = useNavigate();
  if (!task) return null;
  const contact = task.contact_name || "contact";

  // V9.39 — Scheduled outreach surface. Delegated tasks are now
  // schedule-only; show WHEN the first text will fire so the owner
  // never wonders whether Journey already messaged their person.
  let scheduleNote = "";
  if (task.repeat_type === "weekly" || task.repeat_type === "daily") {
    const dt = task.scheduled_send_at
      ? new Date(task.scheduled_send_at)
      : null;
    const when = dt
      ? dt.toLocaleString(undefined, {
          weekday: "short",
          hour: "numeric",
          minute: "2-digit",
        })
      : null;
    scheduleNote = ` · ${task.repeat_type === "weekly" ? "weekly" : "daily"}${
      when ? ` · first ${when}` : ""
    }`;
  } else if (task.scheduled_send_at) {
    const dt = new Date(task.scheduled_send_at);
    scheduleNote = ` · ${dt.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })}`;
  }

  return (
    <button
      onClick={() => navigate("/app/tasks")}
      className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/15 hover:border-cyan-500/50 transition-colors text-xs text-cyan-300"
      data-testid={`task-delegated-chip-${task.id}`}
    >
      <PaperPlaneTilt size={12} weight="duotone" className="text-cyan-400" />
      <span>
        {scheduleNote
          ? "Scheduled for "
          : "Asked "}
        <span className="text-cyan-200 font-medium">{contact}</span>
        {scheduleNote}
        {!scheduleNote && task.deadline_at ? " · deadline set" : ""}
      </span>
    </button>
  );
}
